// @flow

import React from 'react';
import cn from 'classnames';

import styles from './styles.module.scss';
import {
  formatPostflopAction,
  getPostflopActionPotFraction,
} from '../stepsHandler';

type Props = {
  percentage: any,
  actionContext?: any,
  onSort?: (action: string) => void,
  sortAction?: string | null,
}

const SimilarPercentages = ({
  percentage, actionContext = {}, onSort, sortAction = null,
}: Props) => {
  const call = percentage?.call ? Math.round(percentage?.call) : null;
  const check = percentage?.check ? Math.round(percentage?.check) : null;
  const fold = percentage?.fold ? Math.round(percentage?.fold) : null;
  const raise = percentage?.raise ? Math.round(percentage?.raise) : null;
  const bet = percentage?.bet ? Math.round(percentage?.bet) : null;
  const small = percentage?.small ? Math.round(percentage?.small) : null;
  const big = percentage?.big ? Math.round(percentage?.big) : null;
  const isResult = !call && !fold && !raise && !check && !bet && !small && !big;
  const isDarkSizing = (action) => (
    getPostflopActionPotFraction(action, actionContext) > 1 / 2
  );
  const describeFrequency = (action, frequency) => {
    const label = formatPostflopAction(action, actionContext);
    const actionLabel = label.endsWith('%') ? `${label} pot` : label;
    return `${actionLabel}: ${frequency || 0}% of hands`;
  };

  return (
    <div className={`${styles.wrapper}`}>
      <div className={cn(
        styles.resultProgressBar,
        { [styles.resultProgressBarDisabled]: isResult },
      )}
      >
        {isResult ? '0%' : null}
        <button
          type="button"
          aria-label={describeFrequency('fold', fold)}
          title={describeFrequency('fold', fold)}
          onClick={() => onSort && onSort('fold')}
          aria-pressed={onSort ? sortAction === 'fold' : undefined}
          disabled={!onSort || !fold}
          className={cn(styles.container, styles.fold)}
          style={{ width: `${fold || 0}%` }}
        >
          {fold ? `${fold}%` : null}
        </button>
        <button
          type="button"
          aria-label={describeFrequency('call', call)}
          title={describeFrequency('call', call)}
          onClick={() => onSort && onSort('call')}
          aria-pressed={onSort ? sortAction === 'call' : undefined}
          disabled={!onSort || !call}
          className={cn(styles.container, styles.call)}
          style={{ width: `${call || 0}%` }}
        >
          {call ? `${call}%` : null}
        </button>
        <button
          type="button"
          aria-label={describeFrequency('check', check)}
          title={describeFrequency('check', check)}
          onClick={() => onSort && onSort('check')}
          aria-pressed={onSort ? sortAction === 'check' : undefined}
          disabled={!onSort || !check}
          className={cn(styles.container, styles.call)}
          style={{ width: `${check || 0}%` }}
        >
          {check ? `${check}%` : null}
        </button>
        <button
          type="button"
          aria-label={describeFrequency('small', small)}
          title={describeFrequency('small', small)}
          onClick={() => onSort && onSort('small')}
          aria-pressed={onSort ? sortAction === 'small' : undefined}
          disabled={!onSort || !small}
          className={cn(
            styles.container,
            isDarkSizing('small') ? styles.darkBet : styles.lightBet,
          )}
          style={{ width: `${small || 0}%` }}
        >
          {small ? `${small}%` : null}
        </button>
        <button
          type="button"
          aria-label={describeFrequency('bet', bet)}
          title={describeFrequency('bet', bet)}
          onClick={() => onSort && onSort('bet')}
          aria-pressed={onSort ? sortAction === 'bet' : undefined}
          disabled={!onSort || !bet}
          className={cn(
            styles.container,
            isDarkSizing('bet') ? styles.darkBet : styles.lightBet,
          )}
          style={{ width: `${bet || 0}%` }}
        >
          {bet ? `${bet}%` : null}
        </button>
        <button
          type="button"
          aria-label={describeFrequency('raise', raise)}
          title={describeFrequency('raise', raise)}
          onClick={() => onSort && onSort('raise')}
          aria-pressed={onSort ? sortAction === 'raise' : undefined}
          disabled={!onSort || !raise}
          className={cn(
            styles.container,
            isDarkSizing('raise') ? styles.darkBet : styles.lightBet,
          )}
          style={{ width: `${raise || 0}%` }}
        >
          {raise ? `${raise}%` : null}
        </button>
        <button
          type="button"
          aria-label={describeFrequency('big', big)}
          title={describeFrequency('big', big)}
          onClick={() => onSort && onSort('big')}
          aria-pressed={onSort ? sortAction === 'big' : undefined}
          disabled={!onSort || !big}
          className={cn(
            styles.container,
            isDarkSizing('big') ? styles.darkBet : styles.lightBet,
          )}
          style={{ width: `${big || 0}%` }}
        >
          {big ? `${big}%` : null}
        </button>
      </div>
      <div className={styles.legendWrapper}>

        {fold ? (
          <div className={styles.item}>
            <div className={cn(styles.dot, styles.dotFold)} />
            <div className={styles.name}>
              Fold
            </div>
          </div>
        ) : null}
        {call ? (
          <div className={styles.item}>
            <div className={cn(styles.dot, styles.dotCall)} />
            <div className={styles.name}>
              Call
            </div>
          </div>
        ) : null}
        {check ? (
          <div className={styles.item}>
            <div className={cn(styles.dot, styles.dotCall)} />
            <div className={styles.name}>
              Check
            </div>
          </div>
        ) : null}
        {small ? (
          <div className={styles.item}>
            <div className={cn(styles.dot, styles.dotLightBet)} />
            <div className={styles.name}>
              {formatPostflopAction('small', actionContext)}
            </div>
          </div>
        ) : null}
        {bet ? (
          <div className={styles.item}>
            <div className={cn(
              styles.dot,
              isDarkSizing('bet') ? styles.dotDarkBet : styles.dotLightBet,
            )}
            />
            <div className={styles.name}>
              {formatPostflopAction('bet', actionContext)}
            </div>
          </div>
        ) : null}
        {raise ? (
          <div className={styles.item}>
            <div className={cn(
              styles.dot,
              isDarkSizing('raise') ? styles.dotDarkBet : styles.dotLightBet,
            )}
            />
            <div className={styles.name}>
              {formatPostflopAction('raise', actionContext)}
            </div>
          </div>
        ) : null}
        {big ? (
          <div className={styles.item}>
            <div className={cn(
              styles.dot,
              isDarkSizing('big') ? styles.dotDarkBet : styles.dotLightBet,
            )}
            />
            <div className={styles.name}>
              {formatPostflopAction('big', actionContext)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SimilarPercentages;
